import React from 'react';
import { Star, CheckCircle, Heart, Users, Package, TrendingUp } from 'lucide-react';

export default function BoutiqueRetail() {
  const framework = [
    {
      phase: 'Phase 1: Boutique Network Mapping',
      duration: '1-2 weeks',
      activities: [
        'Target boutique identification by region and aesthetic',
        'Buyer persona development for indie retailers',
        'Product-boutique fit assessment and curation',
        'Pricing and margin analysis for wholesale viability',
        'Initial outreach list prioritization (top 50 targets)'
      ]
    },
    {
      phase: 'Phase 2: Relationship Building',
      duration: '3-4 weeks',
      activities: [
        'Personalized outreach campaigns to boutique owners',
        'Sample program execution and feedback collection',
        'Line sheet development with compelling storytelling',
        'Terms negotiation (payment, minimums, exclusivity)',
        'First order conversion and onboarding'
      ]
    },
    {
      phase: 'Phase 3: Launch & Support',
      duration: '4-6 weeks',
      activities: [
        'Initial inventory shipment and merchandising support',
        'In-store marketing collateral and POS materials',
        'Staff training materials and product education',
        'Social media co-marketing campaign coordination',
        'Sell-through tracking and reorder management'
      ]
    },
    {
      phase: 'Phase 4: Network Expansion',
      duration: 'Ongoing',
      activities: [
        'Performance analysis and best practice identification',
        'Referral program activation (boutiques refer boutiques)',
        'Seasonal line reviews and product refreshes',
        'Regional expansion based on proven markets',
        'Account management and relationship nurturing'
      ]
    }
  ];

  const differentiators = [
    {
      title: 'The Boutique Whisperer Approach',
      icon: Heart,
      description: "Boutique owners aren't motivated by spreadsheets—they're curators building collections that reflect their taste and community. We speak their language: story, aesthetic, and emotional resonance, not SKUs and margins."
    },
    {
      title: 'Established Network Access',
      icon: Users,
      description: 'We maintain relationships with 500+ boutique and specialty retailers across North America. Warm introductions replace cold emails, dramatically increasing response and conversion rates.'
    },
    {
      title: 'Flexible Terms Structure',
      icon: Package,
      description: 'Boutiques operate with tight cash flow. We structure consignment, flexible payment terms, and trial programs that make saying yes easy—reducing barriers to initial orders.'
    },
    {
      title: 'Co-Marketing Amplification',
      icon: TrendingUp,
      description: 'Boutique partnerships create content gold. We coordinate Instagram features, trunk shows, and local events that drive traffic to both stores and your DTC channel—mutual growth, not channel conflict.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-7xl mx-auto px-6 py-16">
        {/* Header */}
        <div className="mb-16">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 bg-gradient-to-br from-[#38A3A5] to-[#38A3A5] rounded-xl flex items-center justify-center">
              <Star size={32} className="text-white" />
            </div>
            <div>
              <h1 className="text-5xl font-bold text-[#000000]">
                Boutique & Specialty Retail
              </h1>
              <p className="text-xl text-gray-600 mt-2">
                Access specialty retail channels
              </p>
            </div>
          </div>
        </div>

        {/* Introduction */}
        <div className="mb-16">
          <div className="bg-gradient-to-br from-gray-50 to-white border-2 border-gray-200 rounded-2xl p-8">
            <h2 className="text-3xl font-bold mb-4 text-[#000000]">The Power of Boutique Distribution</h2>
            <p className="text-lg text-gray-700 mb-4">
              Boutique and specialty retail provides what digital advertising cannot: serendipitous discovery, tactile product experience, and third-party credibility from trusted local curators. When the right boutiques carry your brand, customers discover you organically.
            </p>
            <p className="text-lg text-gray-700">
              But boutique relationships require different skills than big box retail. These are passion-driven business owners who care deeply about their curation. We help brands access this channel authentically while maintaining profitability through intelligent partnership strategies.
            </p>
          </div>
        </div>

        {/* The Motif Unbound Framework */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold mb-4 text-[#000000]">The Boutique Relationship Framework</h2>
          <p className="text-lg text-gray-700 mb-8">
            Breaking into boutique retail isn't about mass outreach—it's about targeted relationship building with shop owners who share your brand values and aesthetic. Our framework prioritizes quality over quantity.
          </p>
          
          <div className="space-y-6">
            {framework.map((phase, index) => (
              <div key={index} className="bg-gradient-to-br from-white to-gray-50 border-2 border-gray-200 rounded-2xl p-8 hover:border-[#38A3A5] transition-all">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 bg-[#38A3A5] rounded-xl flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold text-xl">{index + 1}</span>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-[#000000] mb-2">{phase.phase}</h3>
                    <p className="text-[#38A3A5] font-semibold mb-4">Timeline: {phase.duration}</p>
                  </div>
                </div>
                <ul className="space-y-3 ml-16">
                  {phase.activities.map((activity, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <CheckCircle size={20} className="text-[#38A3A5] flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{activity}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* What Makes Us Different */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold mb-4 text-[#000000]">What Makes Us Different</h2>
          <p className="text-lg text-gray-700 mb-8">
            Most brokers treat boutiques like another sales channel. We understand that boutique owners are curators and tastemakers who need to believe in your brand story before they'll share shelf space.
          </p>
          
          <div className="grid md:grid-cols-2 gap-6">
            {differentiators.map((item, index) => (
              <div key={index} className="bg-white border-2 border-gray-200 rounded-2xl p-8 hover:border-[#38A3A5] transition-all">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#38A3A5] to-[#38A3A5] rounded-xl flex items-center justify-center flex-shrink-0">
                    <item.icon size={24} className="text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-[#000000]">{item.title}</h3>
                </div>
                <p className="text-gray-700">{item.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="bg-gradient-to-br from-[#38A3A5] to-[#38A3A5] rounded-2xl p-12 text-center text-white">
          <h2 className="text-4xl font-bold mb-4">Ready to Enter Boutique Retail?</h2>
          <p className="text-xl mb-8 opacity-90">
            Let's discuss your brand story and identify the boutiques that align with your values and aesthetic.
          </p>
          <a 
            href="mailto:contact@motifunbound.com?subject=Schedule%20a%20Consult&body=Hi%20Motif%20Unbound%20team,%0D%0A%0D%0AI'd%20like%20to%20schedule%20a%20consultation%20to%20discuss%20our%20market%20expansion.%20Please%20share%20available%20times.%0D%0A%0D%0AThank%20you."
            className="inline-block bg-white text-[#38A3A5] px-8 py-4 rounded-xl font-semibold text-lg hover:bg-gray-100 transition-all"
          >
            Schedule a Consult
          </a>
        </div>
      </div>
    </div>
  );
}
